import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';
import { ConfirmDialogComponent } from '@ams/ui-kit';

export interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'primary' | 'destructive';
}

/**
 * Imperatively shows the ui-kit confirm dialog and resolves to true/false —
 * a programmatic replacement for window.confirm() so non-component code (route
 * guards, etc.) can prompt with our own styling. Dynamically creates an
 * <ams-confirm-dialog>, attaches it to the app, and tears it down on response.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  confirm(options: ConfirmOptions = {}): Promise<boolean> {
    const ref: ComponentRef<ConfirmDialogComponent> = createComponent(ConfirmDialogComponent, {
      environmentInjector: this.injector,
    });

    ref.setInput('open', true);
    ref.setInput('title', options.title ?? 'Are you sure?');
    ref.setInput('description', options.message ?? 'Are you sure you want to continue?');
    ref.setInput('confirmText', options.confirmText ?? 'Confirm');
    ref.setInput('cancelText', options.cancelText ?? 'Cancel');
    ref.setInput('variant', options.variant ?? 'primary');

    document.body.appendChild(ref.location.nativeElement);
    this.appRef.attachView(ref.hostView);

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (result: boolean) => {
        if (settled) return;
        settled = true;
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        resolve(result);
      };
      ref.instance.confirmed.subscribe(() => finish(true));
      ref.instance.cancelled.subscribe(() => finish(false));
      ref.instance.closed.subscribe(() => finish(false));
    });
  }
}
